import _ from 'lodash'
import Backbone from 'backbone'
import selects from 'scoreboard/components/selects'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import arrayToHash from 'scoreboard/components/array-to-hash'

/**
 * Renders a control for choosing tags (String values).
 * See references for examples.
 *
 * The default behavior is to:
 *
 * 1. Only allow distinct tag names (case-insensitive).  To override this you can
 * 	  assign a new function to `options.selectOptions.creatable`
 *
 * 2. Set a "Add Tags..." placeholder.  To override assign `options.selectOptions.placeholder`
 *
 * While this component was designed for Tags, it can also be used to
 * display a select for any user input text values (e.g. Dataset Keys)
 */
var TagsChooser = Backbone.View.extend({

	/**
	 *
	 * @param {Tag[]} [options.tags] tags that should be pre-populated in the display
	 *
	 * @param {Tag[]} [options.globalTags] all existing tags for the select options
	 *
	 * @param {Object} [options.selectOptions] any valid option for scoreboard-select.
	 * Options that are provided here will override the defaults that the tag-chooser
	 * assigns.
	 *
	 * @param {Boolean} [options.checkAllowedTags=true] false indicates that we
	 * don't need to check config.tags for allowed tags
	 * (ex. on the app admin page)
	 */
	initialize: function(options) {
		this.options = options || {}
		this.globalTags = this.options.globalTags
		this.checkAllowedTags = _.get(this.options, 'checkAllowedTags', true)
	},

	render: function() {
		// create a map for O(1) lookups of existing tags
		this.existingTags = arrayToHash(this.options.tags)
		let tags = _.map(this.globalTags, 'label')
		// #14464 be sure to include any tags that haven't been saved yet
		tags = _.union(tags, this.options.tags)
		this._setupTagSelect(tags)
	},

	/**
	 * Creates the filter select for the provided tag options
	 *
     * @param {String[]} tags The possible suggestions for tags
	 */
	_setupTagSelect: function(tags) {
		const lowerTrimmedTags = new Set(
			tags.map(tag => normalizeTag(tag))
		)
		// https://github.com/SpiderStrategies/Scoreboard/issues/16536#issuecomment-378013831
		const sanitizeForConnect = value => value.replace(/[|,]/g, '')

		const defaultOptions = {
			multiple: true,
			render: {
				create: function (d) {
					// The create new option at the bottom
					return i18n.get('common.add2', _.escape(d.label))
				},
				errorMessage: option => {
					return i18n.get('common.error.tagNotAllowed', option.label)
				}
			},
			creatable: tag => {
				if (this.checkAllowedTags && !_.isEmpty(config.tags)) {
					return false // Allowed Tags are defined, can't create new tags
				}
				const value = sanitizeForConnect(tag)
				// #56425
				// Do not allow duplicate tags with leading or trailing spaces
				const exactMatch = lowerTrimmedTags.has(
					normalizeTag(value)
				)
				return !exactMatch && {value, label: value}
			},
			mapOnSelect: item => {
				const value = sanitizeForConnect(item.value)
				item.value = value
				item.label = _.escape(value)
				return item
			},
			onCreate: tag => {
				// #56425
				// Do not create tags with leading or trailing spaces
				lowerTrimmedTags.add(normalizeTag(tag.value))
			},
			placeholder: i18n.get('common.add2', 'common.tags') + '...',
			options: _.map(tags, tag => {
				return {
					label: tag,
					value: tag,
					selected: this.existingTags[tag]
				}
			})
		}

		var conditionalOptions = {}
		if (_.isEmpty(config.tags)) {
			// if no fixed list of tags, allow creating new ones
			conditionalOptions.create = function (input) {
				return {
					label: _.escape(input),
					value: input
				}
			}
		}

		selects.filtering(this.el, _.extend(defaultOptions,
			conditionalOptions, this.options.selectOptions))
	}
})

/**
 * Normalize a tag by trimming and converting to lowercase
 * @param str - the tag to normalize
 * @returns {string} - the trimmed and all lowercase tag
 */
function normalizeTag(str) {
	if (!str || typeof str !== 'string') {
		return str
	}
	return str.trim().toLowerCase()
}

export default TagsChooser
export { normalizeTag }
TagsChooser.__filename__ = 'components/tags-chooser.js'
